import React, { useEffect, useRef, useState } from "react";

const prefersReduced = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Animate a number counting up from 0 to `value` on mount / whenever it changes.
 *
 * Non-numeric values (e.g. "4h 30m") render as-is. Honors reduced-motion by
 * jumping straight to the final value.
 *
 *   <CountUp value={93} />
 *   <CountUp value={3827} format={(n) => n.toLocaleString()} />
 */
const CountUp = ({ value, duration = 900, format, className }) => {
  const numeric = Number(value);
  const isNum = value !== "" && value !== null && Number.isFinite(numeric);

  const [display, setDisplay] = useState(isNum ? 0 : value);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!isNum) {
      setDisplay(value);
      return;
    }
    if (prefersReduced() || duration <= 0) {
      setDisplay(numeric);
      return;
    }

    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setDisplay(Math.round(numeric * eased));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(rafRef.current);
  }, [numeric, isNum, value, duration]);

  const out = isNum && format ? format(display) : display;
  return <span className={className}>{out}</span>;
};

export default CountUp;
